import { useQuery } from '@tanstack/react-query';
import { ShoppingCart } from 'lucide-react';
import { Card, CardTitle, CardDescription } from './Card';
import { Skeleton } from './EmptyState';
import { StatusPill } from './StatusPill';
import { api } from '@/lib/api';
import { money, date } from '@/lib/format';

interface ProjectPO {
  id: string;
  name: string;
  supplier_id: string;
  supplier_name?: string;
  transaction_date: string;
  status: string;
  grand_total: string;
}

export function ProjectPurchaseOrdersPanel({ projectID }: { projectID: string }) {
  const { data, isLoading } = useQuery({
    queryKey: ['project-purchase-orders', projectID],
    queryFn:  () => api<{ items: ProjectPO[] }>(`/projects/projects/${projectID}/purchase-orders`),
    enabled:  !!projectID,
  });
  const items = data?.items ?? [];

  if (!isLoading && items.length === 0) return null;

  return (
    <Card padded={false}>
      <div className="px-5 py-3 border-b border-hairline">
        <CardTitle>
          <ShoppingCart className="size-4 inline mr-1.5 text-accent" /> Purchase Orders
        </CardTitle>
        <CardDescription>Purchase Orders charged against this project.</CardDescription>
      </div>

      {isLoading ? (
        <div className="p-5"><Skeleton className="h-20 w-full" /></div>
      ) : (
        <table className="w-full text-body-sm">
          <thead className="bg-surface-soft border-b border-hairline text-micro-uppercase text-stone">
            <tr>
              <th className="text-left font-medium px-4 py-2">PO</th>
              <th className="text-left font-medium px-4 py-2">Supplier</th>
              <th className="text-left font-medium px-4 py-2">Date</th>
              <th className="text-left font-medium px-4 py-2">Status</th>
              <th className="text-right font-medium px-4 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-t border-hairline hover:bg-bg-subtle/40">
                <td className="px-4 py-2">
                  <a href={`/accounting/purchase-orders/${p.id}`} className="font-mono text-caption text-text-primary hover:underline">{p.name}</a>
                </td>
                <td className="px-4 py-2 text-text-primary">{p.supplier_name || p.supplier_id}</td>
                <td className="px-4 py-2 text-text-secondary">{date(p.transaction_date)}</td>
                <td className="px-4 py-2"><StatusPill tone={p.status === 'Completed' ? 'success' : 'accent'} withDot={false}>{p.status}</StatusPill></td>
                <td className="px-4 py-2 text-right num">{money(p.grand_total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
